import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { shortTime } from "./calls";
import { todayIn } from "./pickup";
import {
  defaultNeedBy,
  noteCount,
  NOTE_MAX,
  openRequest,
  personName,
  PORTAL_BY,
  type RequestProblem,
  requestProblem,
  shortDay,
  SOURCE_TEXT,
  statusClass,
  statusDetail,
} from "./requests";
import { type Saved, saveFailure } from "./save";

/** Rule P-6: the pickup requests of one account, and the "Request a pickup" form. */
export function AccountRequests({
  account,
  org,
  auth,
  user,
  ask,
  onSaved,
}: {
  account: api.AccountDetail;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  ask: number;
  onSaved: (next: api.AccountDetail) => void;
}) {
  const today = todayIn(org.time_zone);
  const [open, setOpen] = useState(false);
  const [needBy, setNeedBy] = useState(() => defaultNeedBy(today));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [badField, setBadField] = useState<RequestProblem["field"]>("need_by");
  const entryId = useRef<string | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const needByInput = useRef<HTMLInputElement>(null);
  const statusLine = useRef<HTMLParagraphElement>(null);
  const title = useRef<HTMLHeadingElement>(null);
  const requests = account.pickup_requests ?? [];
  const current = openRequest(requests);
  const canAsk = Boolean(account.can_request_pickup) && current === null;
  useEffect(() => {
    if (open) needByInput.current?.focus();
  }, [open]);
  const lastAsk = useRef(ask);
  useEffect(() => {
    if (ask === lastAsk.current) return;
    lastAsk.current = ask;
    title.current?.scrollIntoView({ block: "start" });
    if (canAsk) {
      setSaved(null);
      setOpen(true);
      needByInput.current?.focus({ preventScroll: true });
    } else title.current?.focus({ preventScroll: true });
  }, [ask]); // Only a new jump opens the form, not a change of the account.
  const close = () => {
    setOpen(false);
    requestAnimationFrame(() => button.current?.focus());
  };
  const edit = (next: () => void) => {
    next();
    entryId.current = null;
    setSaved(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = requestProblem(needBy, note, today);
    if (problem) {
      setBadField(problem.field);
      setSaved({ ok: false, field: true, text: problem.text });
      (problem.field === "note" ? document.getElementById("request-note") : needByInput.current)?.focus();
      return;
    }
    entryId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    try {
      const next = await api.requestPickup(await user.getIdToken(), account.id, { id: entryId.current, need_by: needBy, note: note.trim() });
      entryId.current = null;
      setOpen(false);
      setNote("");
      setNeedBy(defaultNeedBy(today));
      onSaved(next);
      setSaved({ ok: true, text: `Saved: a pickup request by ${shortDay(needBy)}.` });
      requestAnimationFrame(() => statusLine.current?.focus());
    } catch (e) {
      const failure = saveFailure(e, "The request");
      // Only a 422 is about the fields; a 403 or a 409 goes in the status line.
      const status = e instanceof api.ApiError ? e.status : 0;
      setBadField(failure.text.includes("note") ? "note" : "need_by");
      setSaved(!failure.ok && status !== 422 ? { ...failure, field: false } : failure);
    } finally {
      setSaving(false);
    }
  };
  if (account.pickup_requests === null && !account.can_request_pickup) return null;
  const fieldError = saved && !saved.ok && saved.field ? saved.text : null;
  const needByError = badField === "need_by" ? fieldError : null;
  const noteError = badField === "note" ? fieldError : null;
  return (
    <section aria-labelledby="account-requests" data-testid="request-section">
      <h2 id="account-requests" ref={title} tabIndex={-1}>
        Pickup requests
      </h2>
      {account.can_request_pickup && !open && (
        <p className="actions">
          <button
            ref={button}
            type="button"
            data-testid="request-open"
            disabled={current !== null}
            title={current ? `The account has an open request, need by ${shortDay(current.need_by)}.` : undefined}
            onClick={() => {
              setSaved(null);
              setOpen(true);
            }}
          >
            Request a pickup
          </button>
          {current && (
            <span className="sub" data-testid="request-open-note">
              {" "}
              Open request: {statusDetail(current)}, need by {shortDay(current.need_by)}.
            </span>
          )}
        </p>
      )}
      {open && (
        <form onSubmit={submit} noValidate data-testid="request-form" aria-labelledby="request-form-title">
          <h3 id="request-form-title">Request a pickup</h3>
          <label htmlFor="request-need-by">Need by</label>
          <input
            id="request-need-by"
            type="date"
            data-testid="request-need-by"
            min={today}
            value={needBy}
            ref={needByInput}
            onChange={(e) => edit(() => setNeedBy(e.target.value))}
            aria-describedby={needByError ? "request-error" : "request-need-by-hint"}
            aria-invalid={needByError ? true : undefined}
          />
          <p id="request-need-by-hint" className="sub">
            The planner puts the account on a route by this day.
          </p>
          {needByError && (
            <p id="request-error" role="alert" className="error" data-testid="request-error">
              {needByError}
            </p>
          )}
          <label htmlFor="request-note">Note</label>
          <textarea
            id="request-note"
            data-testid="request-note"
            rows={2}
            value={note}
            onChange={(e) => edit(() => setNote(e.target.value))}
            aria-describedby={noteError ? "request-note-error request-note-count" : "request-note-count"}
            aria-invalid={noteError ? true : undefined}
          />
          <p id="request-note-count" className={note.trim().length > NOTE_MAX ? "sub error" : "sub"} data-testid="request-note-count">
            {noteCount(note)}
          </p>
          {noteError && (
            <p id="request-note-error" role="alert" className="error" data-testid="request-error">
              {noteError}
            </p>
          )}
          <div className="actions">
            <button className="primary" data-testid="request-save" disabled={saving}>
              {saving ? "Saving…" : "Save the request"}
            </button>
            <button type="button" data-testid="request-close" disabled={saving} onClick={close}>
              Close
            </button>
          </div>
        </form>
      )}
      <p ref={statusLine} tabIndex={-1} role="status" data-testid="request-status" className={saved && !saved.ok && !saved.field ? "error" : undefined}>
        {saved && !fieldError && saved.text}
        {saved && !saved.ok && saved.session && (
          <>
            {" "}
            <button type="button" onClick={() => signOut(auth)}>
              Sign in again
            </button>
          </>
        )}
      </p>
      {account.pickup_requests === null ? null : requests.length ? (
        <div className="table-scroll" tabIndex={0} role="region" aria-label="Pickup requests of this account">
          <table className="list" data-testid="request-table">
            <thead>
              <tr>
                <th scope="col">Requested</th>
                <th scope="col">Status</th>
                <th scope="col">Need by</th>
                <th scope="col">By</th>
                <th scope="col">From</th>
                <th scope="col">Note</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id} data-testid="request-row" data-status={request.status}>
                  <th scope="row">{shortTime(request.requested_at, org.time_zone)}</th>
                  <td data-label="Status">
                    <span className={statusClass(request.status)}>{statusDetail(request)}</span>
                  </td>
                  <td data-label="Need by">{shortDay(request.need_by)}</td>
                  <td data-label="By">{request.requested_by ? personName(request.requested_by) : (PORTAL_BY[request.source] ?? "–")}</td>
                  <td data-label="From">{SOURCE_TEXT[request.source]}</td>
                  <td data-label="Note" className="wrap">
                    {request.note || "–"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p data-testid="account-no-requests">No pickup requests yet.</p>
      )}
    </section>
  );
}
